import { Injectable } from '@angular/core';
import { Asset, EmbeddedAsset } from './asset.dto';
import { HttpService, ObservableService } from '@damofire/data-angular-commons';
import { logger, MAX_QUERY_RESULTS, Query, SearchResult } from '@damofire/data-commons';
import { IssueService } from '../issue/issue.service';
import merge from 'lodash-es/merge';
import { AuthDtoSignerService } from '@damofire/data-security';

export function getEmbeddedAsset(asset: Asset): EmbeddedAsset {
  const embeddedAsset: EmbeddedAsset = {
    description: asset.description,
    system: asset.system,
    upright: asset.upright,
    _id: asset._id.toString(),
  };
  if (asset.section?.length) {
    embeddedAsset.section = asset.section;
  }

  if (asset.clientCode?.length) {
    embeddedAsset.clientCode = asset.clientCode;
  }

  if (asset.aisle?.length) {
    embeddedAsset.aisle = asset.aisle;
  }

  return embeddedAsset;
}

export function generateAndAssignAssetIds(assets: Asset[]): Asset[] {
  const timestamp: number = new Date().getTime();
  const assetsWithIds: Asset[] = assets.map((asset, index) => (asset._id ? { ...asset } : { ...asset, _id: `${timestamp + index}`, nextAsset: null, previousAsset: null }) as Asset);

  for (let i = 0; i < assetsWithIds.length; i++) {
    const current = assetsWithIds[i];
    if (i > 0) {
      const previous = assetsWithIds[i - 1];
      if (previous.system === current.system) {
        current.previousAsset = +previous._id;
        previous.nextAsset = +current._id;
      }
    }
  }

  return assetsWithIds;
}

@Injectable()
export abstract class AssetService extends ObservableService<Asset> {
  protected constructor(
    protected readonly issueService: IssueService,
    protected readonly http: HttpService,
    protected readonly dtoSigner: AuthDtoSignerService,
  ) {
    super();
  }

  static getAssetDisplayDescription(asset: Asset | EmbeddedAsset): string {
    return `${asset.clientCode || ''} (S${asset.system} / U${asset.upright}) ${asset.section || ''}`.trim();
  }

  abstract get(division: string, id: string | number): Promise<Asset>;
  abstract search(divisionId: string, query: Query): Promise<SearchResult<Asset>>;
  protected abstract dbUpdate(division: string, updated: Asset): Promise<void>;
  abstract create(divisionId: string, assets: Asset[]): Promise<Asset[]>;
  abstract delete(divisionId: string, assets: Asset[]): Promise<void>;
  abstract updateBatch(assets: Asset[]): Promise<void>;

  async getCsv(query: Query): Promise<string> {
    return this.http.getByQueryCsv(`ww-assets`, query, null);
  }

  async markRepaired(divisionId: string, assetIds: number[], organizationId: string): Promise<void> {
    for (const assetId of assetIds) {
      const issues = await this.issueService.search(divisionId, { filters: { 'asset._id': assetId }, size: MAX_QUERY_RESULTS });
      await this.issueService.markAsRepaired(divisionId, issues.result, organizationId);
    }
  }

  async update(division: string, asset: Partial<Asset>, assetIds: string[]): Promise<void> {
    for (const id of assetIds) {
      try {
        const dbAsset = await this.get(division, id);
        merge(dbAsset, asset);
        dbAsset.description = `S${dbAsset.system} / U${dbAsset.upright}`;
        await this.dbUpdate(division, dbAsset);
      } catch (err) {
        logger.error('AssetService - update', { category: 'asset', error: err });
      }
    }
  }

  async getPreviousAsset(division: string, currentAssetId: string | number): Promise<Asset> {
    const assets = (await this.search(division, { filters: { nextAsset: +currentAssetId } })).result;
    return assets.length ? assets[0] : null;
  }
}
